import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, Upload, X, Trash2, Image as ImageIcon, Download, Check, CheckSquare, Square, Lock } from "lucide-react";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

export const PhotoGallery = ({ bucketItemId, bucketItemTitle, isOpen, onClose, isGroupItem = false }) => {
  const { token, user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPremiumLocked, setIsPremiumLocked] = useState(false);
  const fileInputRef = useRef(null);
  
  const { hasPaidAccess } = useEntitlements();

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/items/${bucketItemId}/photos`);
      setPhotos(res.data);
      setIsPremiumLocked(false);
      
      // Parallel blob fetches — serial for-await caused 30s+ grid loads
      const fetches = res.data.map(async (photo) => {
        try {
          const photoRes = await axios.get(`${API}/photos/${photo.id}/download`, { responseType: 'blob' });
          return [photo.id, URL.createObjectURL(photoRes.data)];
        } catch (e) { return null; }
      });
      const settled = await Promise.all(fetches);
      setPhotoUrls(Object.fromEntries(settled.filter(Boolean)));
    } catch (error) {
      if (error.response?.status === 403) {
        setIsPremiumLocked(true);
      } else {
        console.error("Failed to fetch photos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bucketItemId) {
      fetchPhotos();
      // Reset selection when opening
      setSelectionMode(false);
      setSelectedPhotos(new Set());
    }
    
    // Cleanup blob URLs on unmount
    return () => {
      Object.values(photoUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [isOpen, bucketItemId]);

  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, currentName: "" });

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Filter + validate up-front so partial uploads don't surprise the user.
    const valid = [];
    const rejected = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        rejected.push(`${f.name}: not an image`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        rejected.push(`${f.name}: over 10MB`);
        continue;
      }
      valid.push(f);
    }
    if (rejected.length) {
      toast.error(`Skipped ${rejected.length}: ${rejected.slice(0, 2).join(" · ")}${rejected.length > 2 ? "…" : ""}`);
    }
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Cap per-batch to keep R2 happy and avoid stranded uploads.
    const BATCH_LIMIT = 20;
    const queue = valid.slice(0, BATCH_LIMIT);
    if (valid.length > BATCH_LIMIT) {
      toast.info(`Uploading the first ${BATCH_LIMIT} photos — repeat for the rest.`);
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: queue.length, currentName: queue[0]?.name || "" });

    // Sequential upload — backend writes to R2 and rate-limit / bandwidth is
    // kinder than firing 20 parallel multipart POSTs from a phone.
    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < queue.length; i++) {
      const file = queue[i];
      setUploadProgress({ done: i, total: queue.length, currentName: file.name });
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/items/${bucketItemId}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        succeeded += 1;
      } catch (error) {
        failed += 1;
        console.warn(`[photos] upload failed for ${file.name}:`, error?.response?.data?.detail || error?.message);
      }
    }

    if (succeeded > 0 && failed === 0) {
      toast.success(succeeded === 1 ? "Photo uploaded!" : `${succeeded} photos uploaded!`);
    } else if (succeeded > 0 && failed > 0) {
      toast.warning(`Uploaded ${succeeded}, ${failed} failed.`);
    } else {
      toast.error("Couldn't upload your photos. Please try again.");
    }

    fetchPhotos();
    setUploading(false);
    setUploadProgress({ done: 0, total: 0, currentName: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      toast.success("Photo deleted");
      
      // Revoke the blob URL
      if (photoUrls[photoId]) {
        URL.revokeObjectURL(photoUrls[photoId]);
      }
      
      setPhotos(photos.filter(p => p.id !== photoId));
      setPhotoUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[photoId];
        return newUrls;
      });
      setSelectedPhotos(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
      setSelectedPhoto(null);
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleDownload = async (photo) => {
    try {
      // Get the blob URL or fetch fresh
      let blobUrl = photoUrls[photo.id];
      
      if (!blobUrl) {
        const photoRes = await axios.get(`${API}/photos/${photo.id}/download`, {
          responseType: 'blob'
        });
        blobUrl = URL.createObjectURL(photoRes.data);
      }
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = photo.original_filename || `photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Photo downloaded!");
    } catch (error) {
      toast.error("Failed to download photo");
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedPhotos.size === 0) {
      toast.error("No photos selected");
      return;
    }

    setDownloading(true);
    try {
      const selectedPhotosList = photos.filter(p => selectedPhotos.has(p.id));
      
      for (const photo of selectedPhotosList) {
        await handleDownload(photo);
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      toast.success(`Downloaded ${selectedPhotos.size} photo${selectedPhotos.size > 1 ? 's' : ''}!`);
      setSelectionMode(false);
      setSelectedPhotos(new Set());
    } catch (error) {
      toast.error("Failed to download some photos");
    } finally {
      setDownloading(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const selectAllPhotos = () => {
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos.map(p => p.id)));
    }
  };

  const canDeletePhoto = (photo) => {
    return photo.created_by === user?.id;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-none rounded-2xl p-0 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5 dark:border-white/10">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="font-serif text-xl text-charcoal dark:text-white">
                Memory Gallery
              </DialogTitle>
              <p className="text-sm text-muted-foreground">{bucketItemTitle}</p>
              {isGroupItem && (
                <p className="text-xs text-primary mt-1">Group members can view and download all photos</p>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-4">
              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="photo-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full btn-press"
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <>
                      {uploadProgress.total > 1
                        ? `Uploading ${uploadProgress.done + 1}/${uploadProgress.total}…`
                        : "Uploading…"}
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Add Photos
                    </>
                  )}
                </Button>
                
                {photos.length > 0 && (
                  <>
                    {selectionMode ? (
                      <>
                        <Button
                          onClick={selectAllPhotos}
                          variant="outline"
                          className="rounded-full"
                          data-testid="select-all-btn"
                        >
                          {selectedPhotos.size === photos.length ? (
                            <>
                              <Square className="w-4 h-4 mr-2" />
                              Deselect All
                            </>
                          ) : (
                            <>
                              <CheckSquare className="w-4 h-4 mr-2" />
                              Select All
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={handleDownloadSelected}
                          disabled={selectedPhotos.size === 0 || downloading}
                          className="bg-secondary text-white hover:bg-secondary/90 rounded-full"
                          data-testid="download-selected-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {downloading ? "Downloading..." : `Download (${selectedPhotos.size})`}
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectionMode(false);
                            setSelectedPhotos(new Set());
                          }}
                          variant="ghost"
                          className="rounded-full"
                          data-testid="cancel-selection-btn"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setSelectionMode(true)}
                        variant="outline"
                        className="rounded-full"
                        data-testid="select-photos-btn"
                      >
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Select Photos
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Photo Grid */}
              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-medium text-charcoal dark:text-white mb-2">No photos yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Capture your memories by adding photos
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      className={`aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer transition-all relative group ${
                        selectionMode && selectedPhotos.has(photo.id) 
                          ? 'ring-3 ring-primary ring-offset-2' 
                          : 'hover:opacity-90'
                      }`}
                      onClick={() => {
                        if (selectionMode) {
                          togglePhotoSelection(photo.id);
                        } else {
                          setSelectedPhoto(photo);
                        }
                      }}
                      data-testid={`photo-${photo.id}`}
                    >
                      {photoUrls[photo.id] ? (
                        <img
                          src={photoUrls[photo.id]}
                          alt={photo.original_filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      
                      {/* Selection checkbox */}
                      {selectionMode && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            selectedPhotos.has(photo.id) 
                              ? 'bg-primary text-white' 
                              : 'bg-white/80 text-charcoal border border-charcoal/20'
                          }`}>
                            {selectedPhotos.has(photo.id) && <Check className="w-4 h-4" />}
                          </div>
                        </div>
                      )}
                      
                      {/* Action overlay - only show when not in selection mode */}
                      {!selectionMode && (
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {/* Download button - available to all group members */}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(photo);
                            }}
                            className="h-10 w-10 rounded-full bg-white/20 text-white hover:bg-primary hover:text-white"
                            data-testid={`download-photo-${photo.id}`}
                            title="Download photo"
                          >
                            <Download className="w-5 h-5" />
                          </Button>
                          
                          {/* Delete button - only for photo owner */}
                          {canDeletePhoto(photo) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(photo.id);
                              }}
                              className="h-10 w-10 rounded-full bg-white/20 text-white hover:bg-destructive hover:text-white"
                              data-testid={`delete-photo-${photo.id}`}
                              title="Delete photo"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Full Photo View */}
        {selectedPhoto && photoUrls[selectedPhoto.id] && (
          <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
            <DialogContent className="max-w-4xl bg-black/90 border-none p-2">
              <div className="relative">
                <img
                  src={photoUrls[selectedPhoto.id]}
                  alt={selectedPhoto.original_filename}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />
                
                {/* Top action bar */}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDownload(selectedPhoto)}
                    className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-primary/80"
                    title="Download photo"
                    data-testid="fullview-download-btn"
                  >
                    <Download className="w-5 h-5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setSelectedPhoto(null)}
                    className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Photo info bar */}
                <div className="absolute bottom-2 left-2 right-2 bg-black/60 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-white/80 text-sm truncate">
                    {selectedPhoto.original_filename}
                  </span>
                  <span className="text-white/60 text-xs">
                    {new Date(selectedPhoto.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
      
      {/* Premium Paywall */}
      <PaywallScreen
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type={PaywallType.MEMORY_VAULT}
        triggerSource="photo_gallery"
      />
    </Dialog>
  );
};

export default PhotoGallery;
